<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="800px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card :height="isEditable ? 440 : 'auto'">
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template>
                        Ustawienia kolorystyki {{ side }}
                    </template>
                </h1>
            </v-card-title>
            <v-card-text>
                <v-menu
                    v-if="isEditable"
                    offset-y
                >
                    <template #activator="{on,attrs}">
                        <v-btn
                            color="secondary"
                            v-bind="attrs"
                            depressed
                            data-sentry-label="Dodaj"
                            :disabled="availableColors.length === 0"
                            v-on="on"
                        >
                            <v-icon left>
                                mdi-plus
                            </v-icon>
                            Dodaj
                        </v-btn>
                    </template>
                    <v-list dense>
                        <v-list-item
                            v-for="(color, index) in availableColors"
                            :key="index"
                            @click="addColor(color)"
                        >
                            <v-list-item-title>{{ color.name }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <v-data-table
                    v-if="isEditable"
                    :items="colors"
                    :headers="headers"
                    disable-pagination
                    hide-default-footer
                    :mobile-breakpoint="0"
                    no-data-text="Nie dodano kolorów"
                >
                    <template #item.type="{item}">
                        {{ item.calculationPrintColour.type }}
                    </template>
                    <template #item.name="{item}">
                        {{ item.name }}
                    </template>
                    <template #item.coverage="{item}">
                        <v-edit-dialog
                            :return-value.sync="item.usedPercent"
                            large
                            cancel-text="Anuluj"
                            save-text="Zapisz"
                        >
                            {{ item.usedPercent }}
                            <v-icon color="blue" small>
                                mdi-pencil
                            </v-icon>
                            <template #input>
                                <v-text-field
                                    v-model="item.usedPercent"
                                    v-mask="'###'"
                                    single-line
                                />
                            </template>
                        </v-edit-dialog>
                    </template>
                    <template #item.pmsType="{item}">
                        <v-select
                            v-if="item.calculationPrintColour.type == 'PMS'"
                            v-model="item.pmsTypeId"
                            :items="colorsPmsType[item.id] || filteredMaterialsPmsTypes"
                            item-text="name"
                            item-value="id"
                            :menu-props="{closeOnContentClick: false}"
                            :error="pmsTypeError(item)"
                            hide-details
                            dense
                            outlined
                            @change="calculateFilteredMaterialsPmsTypes(item, true)"
                        />
                    </template>
                    <template #item.description="{item}">
                        <v-edit-dialog
                            :return-value.sync="item.description"
                            large
                            cancel-text="Anuluj"
                            save-text="Zapisz"
                        >
                            {{ item.description }}
                            <v-icon :color="isDescriptionError(item) ? 'red' : 'blue'" small>
                                mdi-pencil
                            </v-icon>
                            <template #input>
                                <v-text-field
                                    v-model="item.description"
                                    single-line
                                />
                            </template>
                        </v-edit-dialog>
                    </template>
                    <template #item.actions="{item}">
                        <div class="d-inline-flex">
                            <v-tooltip :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <v-btn
                                        icon
                                        data-sentry-label="Usuń"
                                        v-on="on"
                                        @click="removeColor(item)"
                                    >
                                        <v-icon color="pink">
                                            mdi-delete
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>Usuń</span>
                            </v-tooltip>
                        </div>
                    </template>
                </v-data-table>

                <v-data-table
                    v-else
                    :items="colors"
                    :headers="readOnlyHeaders"
                    disable-pagination
                    hide-default-footer
                    :mobile-breakpoint="0"
                    no-data-text="Nie dodano kolorów"
                >
                    <template #item.type="{item}">
                        {{ item.calculationPrintColour.type }}
                    </template>
                    <template #item.name="{item}">
                        {{ item.name }}
                    </template>
                    <template #item.coverage="{item}">
                        {{ item.usedPercent }}
                    </template>
                    <template #item.description="{item}">
                        {{ item.description ? item.description : '-' }}
                    </template>
                </v-data-table>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    v-if="isEditable"
                    color="error"
                    text
                    data-sentry-label="Anuluj"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-btn
                    v-else
                    color="info"
                    text
                    data-sentry-label="Zamknij"
                    @click="onClose"
                >
                    Zamknij
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="isEditable"
                    color="success"
                    text
                    data-sentry-label="Zapisz"
                    @click="onSave"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { appConfig } from '@/config'
import tooltipDelay from '@/mixins/tooltip'
import { mask } from 'vue-the-mask'
import { requiredIf } from 'vuelidate/lib/validators'

export default {
    name: 'ColorSettingsDialog',
    directives: { mask },
    mixins: [ tooltipDelay ],
    props: {
        isOpen: {
            type: Boolean
        },
        isEditable: {
            type: Boolean
        },
        sideData: {
            type: String,
            default: null
        },
        impositionData: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            colors: [],
            colorsPmsType: {},
            side: null,
            headers: [
                {
                    text: 'Typ',
                    value: 'type',
                    align: 'left',
                    sortable: false,
                    width: 120,
                    showOnReadOnly: true
                },
                {
                    text: 'Kolor',
                    value: 'name',
                    align: 'center',
                    sortable: false,
                    width: 150,
                    showOnReadOnly: true
                },
                {
                    text: 'Pokrycie (%)',
                    value: 'coverage',
                    align: 'center',
                    sortable: false,
                    width: 110,
                    showOnReadOnly: true
                },
                {
                    text: 'Opis',
                    value: 'description',
                    align: 'left',
                    sortable: false,
                    showOnReadOnly: true
                },
                {
                    text: 'Rodzaj',
                    value: 'pmsType',
                    align: 'center',
                    sortable: false,
                    showOnReadOnly: true
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'right',
                    sortable: false,
                    width: 100,
                    showOnReadOnly: false
                },
            ]
        }
    },
    computed: {
        materialsPmsTypes() {
            return this.$store.state.configurations.materials.materialsPmsTypes
        },
        filteredMaterialsPmsTypes() {
            return this.materialsPmsTypes.filter(item => item.isVisible)
        },
        availableColors() {
            const colors = JSON.parse(JSON.stringify(this.$store.state.calculations.calculations.calculationPrintColors))
            const CMYKList = ['C', 'Y', 'M', 'K']
            const colorsNames = []

            colors.forEach(color => colorsNames.push(color.name))

            if (colors.length >= 4 && CMYKList.every((element) => colorsNames.includes(element))) {
                colors.unshift({
                    name: 'CMYK'
                })
            }

            return colors
        },
        readOnlyHeaders() {
            return this.headers.filter(item => item.showOnReadOnly)
        },
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                this.side = this.sideData
                const colorKey = `calculationImpositionColours${this.side}`

                this.colors = JSON.parse(JSON.stringify(this.impositionData[colorKey]))
                this.handleColors()

                this.colors.forEach(item => {
                    if (item.calculationPrintColour.type === 'PMS') {
                        this.calculateFilteredMaterialsPmsTypes(item)
                    }
                })
            }
        }
    },
    validations() {
        return {
            colors: {
                $each: {
                    pmsTypeId: {
                        required: requiredIf(value => value.calculationPrintColour.type === 'PMS')
                    },
                    description: {
                        required: requiredIf(value => value.calculationPrintColour.type === 'PMS')
                    },
                }
            }
        }
    },
    methods: {
        calculateFilteredMaterialsPmsTypes(item, isChanged = false) {
            if (isChanged) {
                if (this.colorsPmsType[item.id]) {
                    delete this.colorsPmsType[item.id]
                }

                return
            }
            let filtered = JSON.parse(JSON.stringify(this.filteredMaterialsPmsTypes))

            if (item.pmsTypeId && this.filteredMaterialsPmsTypes.find(type => type.id === item.pmsTypeId) === undefined) {
                filtered.push(item.pmsType)
                this.colorsPmsType[item.id] = filtered
            }
        },
        isDescriptionError(item) {
            return this.$v.colors.$dirty && !item.description && this.$v.colors.$each[item.index].$invalid
        },
        addColor(color) {
            if (color.name === 'CMYK') {
                this.addAllCMYKColors()
            } else {
                this.colors.push({
                    calculationPrintColour: color,
                    calculationPrintColourId: color.id,
                    side: this.side,
                    name: color.name,
                    description: null,
                    usedPercent: this.setDefaultUsedPercentHandler(color.type)
                })
            }

            this.handleColors(true)
        },
        removeColor(color) {
            this.colors.splice(color.index, 1)
            this.handleColors()
        },
        addAllCMYKColors() {
            const basicColors = this.$store.state.calculations.calculations.calculationPrintColors.filter(item => item.type === 'zwyczajny')

            this.colors = this.colors.concat(basicColors.map(color => {
                return {
                    calculationPrintColour: color,
                    calculationPrintColourId: color.id,
                    side: this.side,
                    name: color.name,
                    description: null,
                    usedPercent: this.setDefaultUsedPercentHandler(color.type)
                }
            }))
        },
        setDefaultUsedPercentHandler(colorType) {
            switch (colorType) {
                case 'zwyczajny':
                    return appConfig.common.defaultColorUsedPercents.basic
                case 'PMS':
                    return appConfig.common.defaultColorUsedPercents.pms
                case 'lakier':
                    return appConfig.common.defaultColorUsedPercents.lacquer
                default:
                    return 0
            }
        },
        handleColors() {
            let pmsColorCount = 0

            this.colors = this.colors.map((color, index) => {
                const isPMSColor = color.calculationPrintColour.id === 5 // Id Pantone

                if (isPMSColor) pmsColorCount++

                return {
                    ...color,
                    name: isPMSColor ? `${color.name.split(' ')[0]} ${pmsColorCount}` : color.name,
                    index
                }
            })

        },
        pmsTypeError(item) {
            return !item.pmsTypeId && this.$v.colors.$dirty
        },
        onSave() {
            this.$v.colors.$touch()

            if (this.$v.colors.$invalid) {
                this.showInvalidMessage()

                return
            }

            this.$emit('on-save', {
                imposition: this.impositionData,
                colorSettings: this.colors,
                side: this.side
            })

            this.onClose()
        },
        onClose() {
            setTimeout(() => {
                this.colors = []
                this.side = null

            }, 500)

            this.$v.$reset()
            this.$emit('on-close')
        },
        showInvalidMessage() {
            let invalidFields = []

            this.$v.colors.$model.forEach((item, index) => {
                if (this.$v.colors.$each[item.index].$invalid) {
                    if (this.$v.colors.$each[index].pmsTypeId.$invalid) {
                        invalidFields.push('pmsTypeId')
                    }
                    if (this.$v.colors.$each[index].description.$invalid) {
                        invalidFields.push('description')
                    }
                }
            })

            if (invalidFields.length > 1 || invalidFields.includes('pmsTypeId')) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })
            } else if (invalidFields.includes('description')) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message:'Podaj opis PMS'
                })
            }
        }
    }
}
</script>